import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiUserLine,
  RiShieldUserLine,
  RiImageLine,
  RiUploadCloud2Line,
  RiTeamLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/agent`;
const UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET;
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;

const defaultForm = {
  name: "",
  number: "",
  email: "",
  address: "",
  position: "Agent",
  profilePic: "",
};

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const fieldIconMap = {
  name: RiUserLine,
  number: RiPhoneLine,
  email: RiMailLine,
  address: RiMapPinLine,
  position: RiShieldUserLine,
};

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
}) => {
  const Icon = fieldIconMap[name] || RiUserLine;
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder || label}
          className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
        />
      </div>
    </div>
  );
};

const ProfilePicUpload = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", UPLOAD_PRESET);

      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        formData,
      );
      onChange(res.data.secure_url);
    } catch {
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="col-span-2 flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        Profile Picture
      </label>

      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full border border-blue-200 bg-blue-50 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
          {value ? (
            <img
              src={value}
              alt="Profile"
              className="w-full h-full object-contain"
            />
          ) : (
            <RiImageLine className="w-7 h-7 text-blue-300" />
          )}
        </div>

        <div className="flex-1 flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-blue-200 hover:border-blue-400 bg-blue-50/60 hover:bg-blue-50 rounded-lg py-2.5 text-sm text-blue-500 hover:text-blue-700 font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <RiUploadCloud2Line className="w-4 h-4" />
                {value ? "Change Photo" : "Upload Photo"}
              </>
            )}
          </button>

          {value && (
            <div className="flex items-center gap-2">
              <p className="text-[11px] text-slate-400 truncate flex-1">
                {value}
              </p>
              <button
                type="button"
                onClick={() => onChange("")}
                title="Remove photo"
                className="text-red-400 hover:text-red-600 transition flex-shrink-0"
              >
                <RiCloseLine className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formFields = [
  { label: "Full Name", name: "name" },
  { label: "Phone Number", name: "number", type: "tel" },
  { label: "Email", name: "email", type: "email" },
  { label: "Address", name: "address" },
  { label: "Position", name: "position" },
];

const FormBody = ({ form, handleChange, handleProfilePicChange }) => (
  <>
    <div className="grid grid-cols-2 gap-4 mb-4">
      {formFields.map((f) => (
        <Field
          key={f.name}
          {...f}
          value={form[f.name]}
          onChange={handleChange}
        />
      ))}
    </div>

    <div className="grid grid-cols-2 gap-4 mb-6">
      <ProfilePicUpload
        value={form.profilePic}
        onChange={handleProfilePicChange}
      />
    </div>
  </>
);

export default function Agent() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/all-agents`);
      setAgents(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch agents", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const openCreate = () => {
    setForm(defaultForm);
    setModal("create");
  };
  const openEdit = (agent) => {
    setSelected(agent);
    setForm({ ...defaultForm, ...agent });
    setModal("edit");
  };
  const openDelete = (agent) => {
    setSelected(agent);
    setModal("delete");
  };
  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };
  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleProfilePicChange = (url) =>
    setForm((f) => ({ ...f, profilePic: url }));

  const handleCreate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/create-agent`, form);
      notify("Agent created successfully");
      fetchAgents();
      closeModal();
    } catch {
      notify("Failed to create agent", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/agent/${selected._id}`, form);
      notify("Agent updated successfully");
      fetchAgents();
      closeModal();
    } catch {
      notify("Failed to update agent", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/agent/${selected._id}`);
      notify("Agent deleted successfully");
      fetchAgents();
      closeModal();
    } catch {
      notify("Failed to delete agent", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = agents.filter(
    (a) =>
      (a.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.number || "").toLowerCase().includes(search.toLowerCase()),
  );

  const tableHeaders = [
    { label: "Photo" },
    { label: "Name", Icon: RiUserLine },
    { label: "Position", Icon: RiShieldUserLine },
    { label: "Phone", Icon: RiPhoneLine },
    { label: "Email", Icon: RiMailLine },
    { label: "Address", Icon: RiMapPinLine },
    { label: "Actions" },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiTeamLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Agents
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Agent Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agents…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>

            <button
              onClick={fetchAgents}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Agent
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiTeamLine className="w-4 h-4 text-blue-500" />
              All Agents
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading agents…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiTeamLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No agents match your search."
                            : "No agents found. Add one to get started."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((agent, i) => (
                    <tr
                      key={agent._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3">
                        {agent.profilePic ? (
                          <img
                            src={agent.profilePic}
                            alt={agent.name}
                            className="w-9 h-9 rounded-full object-contain border border-blue-100 shadow-sm"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold shadow-sm shadow-blue-200">
                            {(agent.name || "?")[0].toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {agent.name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          {agent.position || "Agent"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {agent.number || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {agent.email || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 max-w-[180px] truncate">
                        {agent.address || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 transition">
                          <button
                            onClick={() => openEdit(agent)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => openDelete(agent)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add New Agent
            </>
          }
          onClose={closeModal}
        >
          <FormBody
            form={form}
            handleChange={handleChange}
            handleProfilePicChange={handleProfilePicChange}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Create Agent
            </button>
          </div>
        </Modal>
      )}

      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
              {selected?.name}
            </>
          }
          onClose={closeModal}
        >
          <FormBody
            form={form}
            handleChange={handleChange}
            handleProfilePicChange={handleProfilePicChange}
          />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete <span className="text-red-500">{selected?.name}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. All data for this agent will be
              permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}{" "}
                Delete Agent
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
